import { useEffect, useState } from "react";

/**
 * Hook to track window visibility state
 * Returns true when the document is visible, false when hidden (tab is inactive)
 */
export const useWindowVisibility = (): boolean => {
  const [isVisible, setIsVisible] = useState<boolean>(() => document.visibilityState === "visible");

  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  return isVisible;
};

/**
 * Hook to track window focus state
 * Returns true when the window has focus, false otherwise
 */
export const useWindowFocus = (): boolean => {
  const [isFocused, setIsFocused] = useState<boolean>(() => document.hasFocus());

  useEffect(() => {
    const handleFocus = () => setIsFocused(true);
    const handleBlur = () => setIsFocused(false);

    window.addEventListener("focus", handleFocus);
    window.addEventListener("blur", handleBlur);

    return () => {
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("blur", handleBlur);
    };
  }, []);

  return isFocused;
};

/**
 * Hook to track if window is "active" (both visible and focused)
 * Returns true only when the window is both visible AND focused
 */
export const useWindowActive = (): boolean => {
  const isVisible = useWindowVisibility();
  const isFocused = useWindowFocus();

  return isVisible && isFocused;
};
